import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useTitle } from '../hooks/useTitle';
import { Store, Tags, Package, Settings as SettingsIcon } from 'lucide-react';
import CategoriesManagement from '../components/Configuration/CategoriesManagement';
import UnitsManagement from '../components/Configuration/UnitsManagement';
import CustomFieldsManagement from '../components/Configuration/CustomFieldsManagement';
import TemplatesManagement from '../components/Configuration/TemplatesManagement';

type Tab = 'categories' | 'units' | 'fields' | 'templates';

const ShopConfigurationPage: React.FC = () => {
  useTitle('Shop Configuration');
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState<Tab>('templates');

  const tabs = [
    { id: 'templates' as Tab, label: 'Shop Templates', icon: Store },
    { id: 'categories' as Tab, label: 'Categories', icon: Tags },
    { id: 'units' as Tab, label: 'Unit Types', icon: Package },
    { id: 'fields' as Tab, label: 'Custom Fields', icon: SettingsIcon },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className={`text-xl sm:text-2xl md:text-3xl font-bold ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>
          Shop Configuration
        </h1>
        <p className={`mt-1 sm:mt-2 text-xs sm:text-sm md:text-base ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
        }`}>
          Customize your shop's categories, units, and product fields
        </p>
      </div>

      {/* Tabs */}
      <div className={`border-b ${
        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
      }`}>
        <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  group inline-flex items-center py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors touch-manipulation
                  ${isActive
                    ? `${theme === 'dark'
                        ? 'border-blue-500 text-blue-500'
                        : 'border-blue-600 text-blue-600'
                      }`
                    : `${theme === 'dark'
                        ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`
                  }
                `}
              >
                <Icon
                  className={`mr-1.5 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5 ${
                    isActive
                      ? theme === 'dark' ? 'text-blue-500' : 'text-blue-600'
                      : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                />
                <span className="hidden xs:inline">{tab.label}</span>
                <span className="xs:hidden">{tab.label.split(' ')[0]}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-4 sm:mt-6">
        {activeTab === 'templates' && <TemplatesManagement />}
        {activeTab === 'categories' && <CategoriesManagement />}
        {activeTab === 'units' && <UnitsManagement />}
        {activeTab === 'fields' && <CustomFieldsManagement />}
      </div>
    </div>
  );
};

export default ShopConfigurationPage;
